import React, { useState, useEffect, useCallback, useMemo } from "react";
import { fetchAllAddOnProducts, AddOnProduct } from "@/api/addOnProducts";
import { Loader2, Package, Check } from "lucide-react";

interface SelectedAddOn {
  id: string;
  name: string;
}

interface AddOnProductSelectorProps {
  value: SelectedAddOn[];
  onChange: (selected: SelectedAddOn[]) => void;
}

export function AddOnProductSelector({
  value,
  onChange,
}: AddOnProductSelectorProps) {
  const [products, setProducts] = useState<AddOnProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadProducts = async () => {
      try {
        setIsLoading(true);
        const response = await fetchAllAddOnProducts();
        if (!cancelled) {
          const activeProducts = response.products.filter((p) => p.isActive);
          setProducts(activeProducts);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load add-on products:", err);
          setError("Failed to load add-on products");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadProducts();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedIds = useMemo(() => {
    return new Set(value.map((v) => v.id));
  }, [value]);

  const handleToggle = useCallback((product: AddOnProduct) => {
    const productId = product.id || "";
    const isSelected = selectedIds.has(productId);

    if (isSelected) {
      onChange(value.filter((v) => v.id !== productId));
    } else {
      onChange([...value, { id: productId, name: product.name }]);
    }
  }, [value, onChange, selectedIds]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading add-on products...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-red-500 text-sm">
        {error}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="py-4 text-gray-500 text-sm">
        No add-on products available.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {products.map((product) => {
        const productId = product.id || "";
        const isSelected = selectedIds.has(productId);

        return (
          <div
            key={productId}
            className={`
              flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors
              ${isSelected
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }
            `}
            onClick={() => handleToggle(product)}
          >
            <div
              className={`
                w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5
                ${isSelected
                  ? "bg-blue-500 border-blue-500"
                  : "border-gray-300 bg-white"
                }
              `}
            >
              {isSelected && <Check className="h-3 w-3 text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium flex items-center gap-2">
                <Package className="h-4 w-4 text-gray-400" />
                {product.name}
              </div>
              {product.description && (
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                  {product.description}
                </p>
              )}
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                <span>
                  {product.price > 0 ? `$${product.price.toFixed(2)}` : "Included"}
                </span>
                <span>
                  {product.purchaseScope === "per_seat" ? "Per Seat" : "Company-wide"}
                </span>
              </div>
            </div>
          </div>
        );
      })}

      {value.length > 0 && (
        <p className="text-sm text-gray-500 pt-2">
          {value.length} add-on{value.length !== 1 ? "s" : ""} selected
        </p>
      )}
    </div>
  );
}
